import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { emit } from '../../platform/analytics/analytics'
import { useScreenViewed } from '../../platform/analytics/useScreenViewed'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { useEntitlement } from '../../platform/access/useEntitlement'
import { Button } from '../../platform/primitives/Button'
import { ButtonPreview } from '../../platform/primitives/ButtonPreview'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { Icon } from '../../platform/icons/Icon'
import { Money } from '../../platform/primitives/Money'
import { useToast } from '../../platform/feedback/ToastProvider'
import { usePageMeta } from '../../shell/PageMeta'
import '../../platform/notes.css'
import { AccountCard, ConnectAnotherTile, GROUP_ICON } from './AccountCard'
import { AccountsSummary, currenciesOf, PerCurrencySubtotals } from './AccountsSummary'
import { accountHealth } from './accountHealth'
import { GROUP_ORDER, groupOf, type Group } from './accountGroups'
import { formatRelative } from './accountsStaleLabels'
import { syncTargetsOf } from './connectionGroups'
import { InstitutionsPanel } from './InstitutionsPanel'
import { balancesForTotals, totalsOf } from './accountMoney'
import { accountRemovalCopy, useAccountDeleteFlow } from './useAccountDeleteFlow'
import { isOverFreeCap, useAccounts, type Account } from './useAccounts'
import { useProviderItems, type ProviderItem } from './useAggregator'
import { useSyncItem, type ItemSyncOutcome } from './useSyncItem'
import { ManualAccountForm } from './ManualAccountForm'
import './accounts.css'

/**
 * The chrome's top-bar subtitle — "N accounts · M institutions · last sync
 * …" — published through `usePageMeta`, the same mechanism `Categories.tsx`/
 * `Merchants.tsx` already use for their own count lines. **Not a second
 * on-canvas heading.** `TopBar.tsx`'s own docblock: the breadcrumb's `<h1>`
 * is "the document's only one", and `Categories.tsx:64` already rejected a
 * duplicate `<h2>` for the identical reason ("breadcrumb already reads
 * 'Organise / Categories'"). The plan's Ticket 1 literally says "Header: h1
 * + subtitle line" — that would be a second page heading, which this
 * codebase has already ruled out twice; this function supplies the
 * subtitle's TEXT and the header keeps only the action row, which
 * `usePageMeta`'s own docblock is exactly the escape hatch for.
 */
function headerSubtitleFor(accounts: Account[]): string {
  const institutionCount = new Set(
    accounts.map((a) => a.institutionName).filter((n): n is string => n !== null && n !== ''),
  ).size
  const parts = [
    `${accounts.length} ${accounts.length === 1 ? 'account' : 'accounts'}`,
    `${institutionCount} ${institutionCount === 1 ? 'institution' : 'institutions'}`,
  ]
  const synced = accounts.map((a) => a.lastSyncedAt).filter((d): d is Date => d !== null)
  if (synced.length > 0) {
    const newest = synced.reduce((max, d) => (d > max ? d : max))
    parts.push(`last sync ${formatRelative(newest)}`)
  }
  return parts.join(' · ')
}

/** Every item a sync was attempted on failed with neither a 409 nor a 410 —
 *  `useSyncItem`'s mutation throws a plain `Error('Sync failed with
 *  ${status}')` (`useSyncItem.ts`'s own docblock: 409/410 are real but
 *  undocumented in `openapi.json`), so the status is read back out of that
 *  message rather than off a structured field this hook does not carry.
 *  `null` for anything that is not that exact shape — a network failure, say
 *  — same discipline `namesReauthRequired`/`transactionCountOf` already
 *  apply to a shape that might not be there. */
function syncFailureStatus(reason: unknown): number | null {
  if (!(reason instanceof Error)) return null
  const match = /Sync failed with (\d+)/.exec(reason.message)
  return match?.[1] ? Number(match[1]) : null
}

/**
 * The "Sync all" summary toast. `skipped` is a NORMAL outcome (M5 Ticket 1's
 * own condition — the 15-minute per-item throttle makes a mixed
 * completed/skipped result the common case), so it is counted alongside
 * `synced` rather than as a failure. 410 ("removed") is called out on its own
 * rather than folded into a generic failure count, because M5 Ticket 1 is
 * explicit that it means relink from scratch, not retry — a fact worth a
 * word of its own in the toast rather than "1 failed".
 */
function summarizeSyncAll(
  results: PromiseSettledResult<ItemSyncOutcome>[],
): { kind: 'success' | 'warning' | 'error'; message: string } {
  let synced = 0
  let skipped = 0
  let reauth = 0
  let removed = 0
  let failed = 0

  for (const result of results) {
    if (result.status === 'fulfilled') {
      if (result.value.skipped) skipped += 1
      else synced += 1
      continue
    }
    const status = syncFailureStatus(result.reason)
    if (status === 409) reauth += 1
    else if (status === 410) removed += 1
    else failed += 1
  }

  const parts: string[] = []
  if (synced > 0) parts.push(`${synced} synced`)
  if (skipped > 0) parts.push(`${skipped} already up to date`)
  if (reauth > 0) parts.push(`${reauth} need${reauth === 1 ? 's' : ''} reconnecting`)
  if (removed > 0) parts.push(`${removed} disconnected — reconnect from scratch`)
  if (failed > 0) parts.push(`${failed} failed`)

  const ok = synced + skipped
  const trouble = reauth + removed + failed
  const kind = trouble === 0 ? 'success' : ok === 0 ? 'error' : 'warning'
  return { kind, message: parts.length > 0 ? `${parts.join(', ')}.` : 'Nothing to sync.' }
}

/**
 * The header's "Sync all" fan-out — client-side, one `useSyncItem().sync()`
 * per connection, because no bulk endpoint exists (M5 Ticket 1). `items` is
 * `null` while its own surface is loading, empty or errored, which is also
 * when the caller hides the button entirely — a manual-only household has
 * nothing to sync, matching M5-R7's "no sync controls at all" philosophy for
 * the identical case at row level.
 *
 * **`sync_triggered` fires ONCE here, before the fan-out** (B1, architect
 * review, #399) — not once per `sync()` call. An earlier version emitted
 * inside `useSyncItem` itself, so five connections synced by one "Sync all"
 * press produced five events with no record that five was one press: `scope`
 * labelled the intent while the event COUNT reported the fan-out width, and
 * that width was never recoverable downstream because it never left the
 * client. `item_count` is the fan-out width, sent explicitly instead —
 * `item` is a registered `STEMS` entry and `count` a `MEASUREMENTS` suffix,
 * so it needs no vocabulary change.
 */
function useSyncAll(items: ProviderItem[] | null): {
  canRun: boolean
  pending: boolean
  run: () => void
} {
  const { sync } = useSyncItem()
  const toast = useToast()
  const [pending, setPending] = useState(false)

  const run = () => {
    if (items === null || items.length === 0 || pending) return
    setPending(true)
    emit('sync_triggered', { scope: 'all', item_count: items.length })
    void Promise.allSettled(items.map((item) => sync(item.id))).then((results) => {
      setPending(false)
      toast.show(summarizeSyncAll(results))
    })
  }

  return { canRun: items !== null && items.length > 0, pending, run }
}

/**
 * The group heading — #425 §A4: icon in a tinted square, label, a count
 * pill, a `1px --line` rule filling the gap, and the group's own total,
 * right-aligned. Replaces `SectionHeader` (`ListRow.tsx`), which gives
 * neither an icon nor a total; `SectionHeader` itself is untouched and stays
 * in use elsewhere (`Connections.tsx`, `Household.tsx`, `CategoryDetail.tsx`).
 *
 * **The total reuses `totalsOf()` on this GROUP's own accounts, not a new
 * function.** `accountMoney.ts` is reused unchanged for this ticket — every
 * account in one group shares one side of the asset/liability split (a group
 * is either all-asset or, for `credit`, all-liability), so `totalsOf(rows)
 * .netWorthCents` is exactly that group's own signed total, and `totalsOf`'s
 * existing `currencies.size > 1 → null` guard is already the "omit or fan
 * out on mixed currency, never sum across" discipline #425 asks for — the
 * SAME discipline `AccountsSummary.tsx`'s three money tiles already apply,
 * reused via `currenciesOf`/`PerCurrencySubtotals` rather than a second
 * mixed-currency fallback invented for groups.
 *
 * **`hasBalance` guards a fabricated `$0.00`.** `totalsOf([])` and
 * `totalsOf([{ currentCents: null, … }])` both return a non-null `Totals`
 * with every figure at 0 (`accountMoney.ts`'s own `withBalance` filter
 * empties out, so the mixed-currency guard never fires) — a freshly-linked
 * group whose one account's balance has not arrived yet would otherwise
 * print a confident, wrong "$0.00" total. `InstitutionsPanel.tsx`'s row
 * total already carries this exact guard for the identical reason (gate
 * finding, #425 T3, blocking 4 — the same fix, 40 lines away, that this
 * function's own docblock cited `totalsOf` reuse as precedent for while
 * dropping); reused here rather than re-derived.
 */
function GroupHeading({
  group,
  title,
  rows,
}: {
  group: Group
  title: string
  rows: Account[]
}) {
  const totals = totalsOf(rows)
  const currencies = currenciesOf(rows)
  const hasBalance = balancesForTotals(rows).length > 0
  return (
    <div className="account-group__heading">
      <span className="account-group__icon">
        <Icon glyph={GROUP_ICON[group]} />
      </span>
      <h3 className="account-group__title">{title}</h3>
      <span className="account-group__count">{rows.length}</span>
      <span className="account-group__rule" aria-hidden="true" />
      <span className="account-group__total">
        {hasBalance &&
          (totals ? (
            <Money cents={totals.netWorthCents} currency={totals.currency ?? undefined} kind="stock" />
          ) : (
            <PerCurrencySubtotals accounts={rows} currencies={currencies} pick={(t) => t.netWorthCents} />
          ))}
      </span>
    </div>
  )
}

const SKELETON = (
  <>
    <Skeleton height={80} />
    <Skeleton height={44} />
    <Skeleton height={44} />
    <Skeleton height={44} />
  </>
)

const READ_ONLY_NOTE =
  'Read-only bank links — TrackE5 can see balances and transactions and can never move money.'

/**
 * What the capped free user sees blurred behind the gate: the Connect button's
 * skin and label on a `<span>`, which holds no control at all (#527). The live
 * button, passed as `preview`, had only `inert` between a capped user and
 * `/accounts/connect`, and `inert` is a hint a directly dispatched click
 * ignores. Skin hook, variant, size and label are shared with the live button
 * (the constants below), so the likeness cannot drift.
 */
const CONNECT_VARIANT = 'outline'
const CONNECT_SIZE = 'page'
export const CONNECT_LABEL = '＋ Connect account'
/** The empty state's action. Exported so the Help article that names it is checked against it. */
export const ADD_ACCOUNT_LABEL = '＋ Add account'

function ConnectPreview() {
  return (
    <ButtonPreview variant={CONNECT_VARIANT} size={CONNECT_SIZE}>
      {CONNECT_LABEL}
    </ButtonPreview>
  )
}

export function Accounts() {
  const state = useAccounts()
  const entitled = useEntitlement('unlimited_accounts')
  // Header subtitle — published to the chrome's top bar via `usePageMeta`,
  // never rendered as a second on-canvas heading. See `headerSubtitleFor`'s
  // own docblock for why this deviates from the plan's literal "h1 +
  // subtitle" wording. `null` while there is nothing to say yet, matching
  // `Categories.tsx`/`Merchants.tsx`'s identical branching.
  usePageMeta(
    state.status === 'success' || state.status === 'stale'
      ? headerSubtitleFor(state.data)
      : state.status === 'empty'
        ? headerSubtitleFor([])
        : null,
  )
  const itemsState = useProviderItems()
  const items =
    itemsState.status === 'success' || itemsState.status === 'stale' ? itemsState.data : null
  const accountList =
    state.status === 'success' || state.status === 'stale' ? state.data : state.status === 'empty' ? [] : null
  // See `syncTargetsOf`: null (no Sync all) while the accounts or items are unknown.
  const syncTargets = syncTargetsOf(accountList, items)
  const syncAll = useSyncAll(syncTargets)
  // `Account | null` now, not `'create' | Account | null` — #397 moved
  // account CREATION behind `/accounts/connect` (the spec's own "Add an
  // account" flow diagram: "＋ Add account" is the entry to a picker between
  // Link a bank and Add manually, not a direct shortcut to this form). This
  // state is left owning EDIT only, which stays here — editing an existing
  // manual account has nothing to do with the connect flow.
  const [editing, setEditing] = useState<Account | null>(null)
  const navigate = useNavigate()
  const toast = useToast()
  const deleteFlow = useAccountDeleteFlow((account) =>
    toast.show({ kind: 'success', message: accountRemovalCopy(account).toast }),
  )

  const copy = deleteFlow.target ? accountRemovalCopy(deleteFlow.target) : null

  // `error_count` is `accountHealth().kind === 'reauth'`, the SAME interpreter
  // `AccountCard`'s status pill and `Connections.tsx` read — a second "needs
  // attention" predicate here would be a second place to get it wrong. What
  // "viewed" means: `useScreenViewed`'s docblock.
  useScreenViewed(state, (accounts) => {
    const manualCount = accounts.filter((a) => a.provider === 'manual').length
    emit('accounts_viewed', {
      account_count: accounts.length,
      connected_count: accounts.length - manualCount,
      manual_count: manualCount,
      error_count: accounts.filter((a) => accountHealth(a).kind === 'reauth').length,
    })
  })

  return (
    <>
      <Async
        surface="accounts"
        state={state}
        skeleton={SKELETON}
        empty={{
          title: 'No accounts yet',
          body: READ_ONLY_NOTE,
          action: { label: ADD_ACCOUNT_LABEL, onAct: () => navigate('/accounts/connect') },
        }}
        announce={(accounts) => {
          return headerSubtitleFor(accounts)
        }}
      >
        {(accounts) => {
          // isOverFreeCap is the ONE definition of this rule (useAccounts.ts's
          // own docblock — architect review, #397 B3): Connect.tsx reads the
          // identical predicate now, rather than a second, disagreeing one.
          const gated = !entitled && isOverFreeCap(accounts)

          const connectButton = (
            <Button
              variant={CONNECT_VARIANT}
              size={CONNECT_SIZE}
              onClick={() => navigate('/accounts/connect')}
            >
              {CONNECT_LABEL}
            </Button>
          )

          return (
            <>
              {/* The header's action row — moved ABOVE the summary tiles,
                  matching the design (M5 Ticket 1). "Import statement" is
                  DELIBERATELY absent — the plan's Deferred table: no such
                  feature exists, and a disabled control would advertise one
                  that does not. */}
              <div className="accounts-toolbar">
                {syncAll.canRun && (
                  <Button variant="chip" onClick={syncAll.run} disabled={syncAll.pending}>
                    {syncAll.pending ? 'Syncing…' : 'Sync all'}
                  </Button>
                )}
                {gated ? (
                  <PremiumGate
                    entitlement="unlimited_accounts"
                    preview={<ConnectPreview />}
                    onUpgrade={() => {
                      /* M13 billing owns the destination; nothing here yet. */
                    }}
                  >
                    {connectButton}
                  </PremiumGate>
                ) : (
                  connectButton
                )}
              </div>

              <AccountsSummary accounts={accounts} />

              <InstitutionsPanel accounts={accounts} />

              {GROUP_ORDER.map(({ group, title }) => {
                const rows = accounts.filter((a) => groupOf(a.type) === group)
                if (rows.length === 0) return null
                return (
                  <div className="account-group" key={group}>
                    <GroupHeading group={group} title={title} rows={rows} />
                    <div className="account-card-grid" role="list" aria-label={title}>
                      {rows.map((account) => (
                        <AccountCard
                          account={account}
                          key={account.id}
                          onEdit={setEditing}
                          onDelete={deleteFlow.start}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}

              {/* §A6 — at the end of a POPULATED list, not only inside the
                  empty state's `READ_ONLY_NOTE` above. Outside every group's
                  own `role="list"` on purpose: an extra `listitem` appended
                  to whichever group happens to render last would put the
                  wrong count behind `getByRole('listitem')` for that group's
                  list (`accounts.spec.ts`'s own `toHaveCount(CASH_NAMES
                  .length)`, say) for a control that is not an account.
                  **Omitted, not gated, once `gated` is true** — the spec
                  names this tile as "also where the free-tier PremiumGate
                  wrapper belongs", but wrapping a SECOND control in the
                  identical gate the toolbar's "＋ Connect account" already
                  carries renders two indistinguishable "Upgrade" buttons on
                  one page (caught by this ticket's own tests) for what is,
                  underneath, the same one entitlement check. The toolbar
                  button is the page's one authoritative gated CTA; this tile
                  stays a convenience echo of it and simply does not render
                  once that CTA is already blurred behind an upgrade prompt.
                  Wrapped in its own `.account-card-grid` (gate finding, #425
                  T3, non-blocking) for the SAME sizing/gap geometry the
                  account cards above use — outside every group's `role=
                  "list"` still, per the paragraph above. */}
              {!gated && (
                <div className="account-card-grid">
                  <ConnectAnotherTile onActivate={() => navigate('/accounts/connect')} />
                </div>
              )}
            </>
          )
        }}
      </Async>

      {editing !== null && (
        <ManualAccountForm editing={editing} onClose={() => setEditing(null)} />
      )}

      <ConfirmDialog
        open={deleteFlow.target !== null}
        onCancel={deleteFlow.cancel}
        onConfirm={deleteFlow.confirm}
        errorFor={deleteFlow.errorFor}
        mode={copy?.mode ?? 'delete_account'}
        title={copy?.title ?? ''}
        body={deleteFlow.target ? deleteFlow.bodyFor(deleteFlow.target) : ''}
        confirmLabel={copy?.confirmLabel ?? 'Delete'}
        destructive
      />
    </>
  )
}
